import { tools } from '@worktango/ai-assistant/react';

import { reportingQueries } from '~/behaviors/queries/reporting.queries';
import { graphqlRequest } from '~/adapters/graphqlRequest';
import {
  FetchSurveyWithSurveyElementsDocument,
  FetchEmployeeAttributesDocument,
  GetSurveyTypeAttributesDocument,
  FetchSurveyTypesWithCategoriesDocument,
  FetchAttributeResponsesDocument,
  FetchEmployeeAttributesQueryResult,
  GetSurveyTypeAttributesQueryResult,
  FetchSurveyTypesWithCategoriesQueryResult,
  FetchAttributeResponsesQueryResult,
} from '~/gql/graphql';
import { coalesce } from '~/helpers/coalesce';

/**
 * Extracts language IDs from survey data, falling back to [1] if none found
 */
export function extractLanguageIds(survey: any): number[] {
  const ids = survey?.namesPerLanguage?.map((lang: any) => lang.language?.id).filter(Boolean);
  return ids && ids.length > 0 ? ids : [1];
}

/**
 * Selects the best available attributes (survey type > general > empty array)
 */
export function selectAttributes(
  surveyTypeAttributes: any,
  generalAttributes: any,
): Array<{ id: number; name: string; values?: any[] }> {
  return surveyTypeAttributes?.surveyTypeAttributes || generalAttributes?.getCompanyConfigDetails?.attributes || [];
}

/**
 * Resolves survey type ID from basic or detailed response
 */
export function resolveSurveyTypeId(basicResponse: any, detailedResponse: any): number | undefined {
  return basicResponse?.types?.[0]?.id || detailedResponse?.types?.[0]?.id;
}

/**
 * Resolves survey name from multiple possible sources
 */
export function resolveSurveyName(surveyData: any, detailedData?: any): string {
  return coalesce(
    surveyData.namesPerLanguage?.[0]?.surveyName || detailedData?.namesPerLanguage?.[0]?.surveyName || surveyData.name,
    'Unnamed Survey',
  );
}

/**
 * Resolves survey type name from survey type info
 */
export function resolveSurveyTypeName(surveyTypeInfo: any, fallbackName?: string): string {
  return coalesce(surveyTypeInfo?.names?.[0]?.name || fallbackName, 'Unknown');
}

export const getBasicSurveyInfoTool = tools.createTool({
  name: 'get_basic_survey_info',
  description: `Get basic information about a survey. This will return: attributes and their values; factors; the survey type; the survey period; the survey configuration; the survey names by language; the survey recipients; questions and their IDs.`,
  displayName: 'Getting survey data',
  pastTenseDisplayName: 'Fetched survey details',
  parameters: (t) =>
    t.Object({
      surveyId: t.Number({
        description: `The numeric ID of the survey to get basic information for.`,
      }),
      isHierarchyReport: t.Optional(
        t.Boolean({
          description:
            "Only affects the people leader attribute. Leave unset for direct-report counts, which is what the reported response counts mean by default. Set true when the user is asking about a leader's full hierarchy: the counts then roll up every level beneath each leader, and leaders whose own reports are all managers appear in the list at all.",
        }),
      ),
    }),
  fulfill: async ({ surveyId, isHierarchyReport }) => {
    try {
      // Fetch both basic survey data and detailed survey data with questions
      const [basicResponse, detailedResponse] = await Promise.all([
        reportingQueries.findSurveys([surveyId]),
        graphqlRequest<any>(FetchSurveyWithSurveyElementsDocument, {
          id: surveyId,
        }),
      ]);

      if (!basicResponse || basicResponse.length === 0) {
        return 'Survey not found. Please check the survey ID and try again.';
      }

      const companyId = coalesce(detailedResponse.data?.survey?.questions?.[0]?.companyId, 0);

      // Get language IDs from the survey
      const languageIds = extractLanguageIds(detailedResponse.data?.survey);

      // Get survey type ID for fetching attributes
      const surveyTypeId = resolveSurveyTypeId(basicResponse[0], detailedResponse.data?.survey);

      // Fetch employee attributes, survey type attributes, and survey types in parallel
      const [generalAttributesResponse, surveyTypeAttributesResponse, surveyTypesResponse, attributeResponsesData] =
        await Promise.all([
          graphqlRequest<FetchEmployeeAttributesQueryResult>(FetchEmployeeAttributesDocument, {
            companyId,
            includePrivateAttributes: true,
          }),
          /* istanbul ignore next -- conditional API call based on surveyTypeId */
          surveyTypeId
            ? graphqlRequest<GetSurveyTypeAttributesQueryResult>(GetSurveyTypeAttributesDocument, {
                companyId,
                surveyTypeId,
                includePrivateAttributes: false,
              })
            : Promise.resolve(null),
          // Left with any because it was requiring too many changes to make it work
          // We can improve it in another PR
          graphqlRequest<any>(FetchSurveyTypesWithCategoriesDocument, {
            companyId,
            /* istanbul ignore start -- defensive fallback for empty languageIds (extractLanguageIds always returns a non-empty list) */
            languageIds: languageIds.length > 0 ? languageIds : [1],
            /* istanbul ignore stop */
            includeBase: true,
          }),
          graphqlRequest<FetchAttributeResponsesQueryResult>(FetchAttributeResponsesDocument, {
            companyIds: [companyId],
            languageId: coalesce(languageIds[0], 1),
            surveyIds: [surveyId],
            isHierarchyReport: Boolean(isHierarchyReport),
            requestManagerHierarchyData: Boolean(isHierarchyReport),
          }),
        ]);

      // Use survey type attributes if available, otherwise fall back to general attributes
      const attributes = selectAttributes(surveyTypeAttributesResponse?.data, generalAttributesResponse?.data);

      return formatSurveyInfoAsNarrative(
        basicResponse[0],
        detailedResponse.data?.survey,
        attributes,
        /* istanbul ignore next -- defensive optional chaining for surveyTypes */
        coalesce(surveyTypesResponse?.data?.companySurveyTypes, []),
        attributeResponsesData?.data?.attributesRatingResponses,
        Boolean(isHierarchyReport),
      );
    } catch (error) {
      console.error('Error fetching survey info:', error);
      return 'An error occurred while fetching survey information. Please try again.';
    }
  },
});

/* istanbul ignore start -- pure narrative formatter: branch-heavy defensive/optional sections, exercised through output assertions in tests */
function formatSurveyInfoAsNarrative(
  surveyData: BasicSurveyData,
  detailedData?: DetailedSurveyData,
  attributes?: EmployeeAttribute[],
  surveyTypes?: Array<{ id: number; names?: Array<{ name?: string }>; threshold?: number }>,
  attributeResponsesData?: any,
  isHierarchyReport?: boolean,
): string {
  const narrative: string[] = [];

  narrative.push('SURVEY BASIC INFORMATION');
  narrative.push('');

  // Basic Details - Use the first available name from namesPerLanguage
  const surveyName = resolveSurveyName(surveyData, detailedData);
  narrative.push(`Survey Name: ${surveyName}`);
  narrative.push(`Survey ID: ${surveyData.id}`);

  // Survey Type Information
  if (surveyData.types && surveyData.types.length > 0) {
    const surveyTypeId = surveyData.types[0].id;
    const surveyTypeInfo = surveyTypes?.find((type) => type.id === surveyTypeId);
    const surveyTypeName = resolveSurveyTypeName(surveyTypeInfo, surveyData.types[0].name);

    narrative.push(`Survey Type: ${surveyTypeName}`);
    narrative.push(`Survey Type ID: ${surveyTypeId}`);

    // Anonymity threshold from survey type
    const threshold = surveyData.types[0].threshold;
    if (threshold !== undefined) {
      narrative.push(`Anonymity Threshold: ${threshold}`);
    }
  }

  // Survey Period - Use detailedData dates if available
  const startDate = detailedData?.startTimeUTC || surveyData.startTimeUTC || surveyData.startDate;
  const endDate = detailedData?.endTimeUTC || surveyData.endTimeUTC || surveyData.endDate;

  if (startDate || endDate) {
    narrative.push('');
    narrative.push('SURVEY PERIOD');

    if (startDate) {
      narrative.push(`Start Date: ${formatDate(startDate)}`);
    }
    if (endDate) {
      narrative.push(`End Date: ${formatDate(endDate)}`);
    }
  }

  // Configuration
  narrative.push('');
  narrative.push('SURVEY CONFIGURATION');

  if (surveyData.anonymousConversation !== undefined) {
    narrative.push(`Anonymous Conversations: ${surveyData.anonymousConversation ? 'Enabled' : 'Disabled'}`);
  }

  if (surveyData.isContinuous !== undefined) {
    narrative.push(`Continuous Survey: ${surveyData.isContinuous ? 'Yes' : 'No'}`);
  }

  // Names per language
  if (surveyData.namesPerLanguage && surveyData.namesPerLanguage.length > 0) {
    narrative.push('');
    narrative.push('SURVEY NAMES BY LANGUAGE');
    surveyData.namesPerLanguage.forEach((item) => {
      const langCode = coalesce(item.language?.langISOCode, 'Unknown');
      const surveyName = coalesce(item.surveyName, 'Unnamed');
      narrative.push(`- ${langCode}: ${surveyName}`);
    });
  }

  // Attributes used in the survey
  if (attributeResponsesData?.attributes && attributeResponsesData.attributes.length > 0) {
    narrative.push('');
    narrative.push('ATTRIBUTES USED IN SURVEY');
    narrative.push(
      isHierarchyReport
        ? "People leader counts below include every level of each leader's hierarchy."
        : "People leader counts below cover each leader's direct reports only.",
    );

    attributeResponsesData.attributes.forEach(
      (attr: {
        count: number;
        id: number;
        name: string;
        spread: {
          max: number;
          min: number;
        };
        values: Array<{
          count: number;
          id: string;
          label: string;
          percentage: number;
          totalCount: number;
        }>;
      }) => {
        if (attr.id && attr.name && attr.values && attr.values.length > 0) {
          // Sort values by count (descending) to show most common first
          const sortedValues = [...attr.values].sort((a, b) => (b.count || 0) - (a.count || 0));

          sortedValues.forEach((value: any) => {
            if (value.label) {
              // The percentage value might already be in percentage format (e.g., 25 for 25%)
              // or it might be in decimal format (e.g., 0.25 for 25%)
              let percentageDisplay = '';
              if (value.percentage !== undefined && value.percentage !== null) {
                const percentage = value.percentage > 1 ? value.percentage : value.percentage * 100;
                percentageDisplay = ` (${Math.round(percentage)}% response rate)`;
              }
              narrative.push(
                `- ${attr.name} (attribute_id: ${attr.id}): ${value.label} - ${value.count} responses${percentageDisplay}`,
              );
            }
          });
        }
      },
    );
  } else {
    // Fallback to checking questions for attributes if no response data
    if (detailedData?.questions) {
      const usedAttributes = new Map<number, Set<string>>();

      detailedData.questions.forEach((question) => {
        if (question.attributes && question.attributes.length > 0) {
          question.attributes.forEach((attr) => {
            if (attr.attributeId) {
              if (!usedAttributes.has(attr.attributeId)) {
                usedAttributes.set(attr.attributeId, new Set());
              }
              if (attr.attributeValue) {
                usedAttributes.get(attr.attributeId)?.add(attr.attributeValue);
              }
            }
          });
        }
      });

      if (usedAttributes.size > 0) {
        narrative.push('');
        narrative.push('ATTRIBUTES USED IN SURVEY');

        if (attributes && attributes.length > 0) {
          attributes.forEach((attr) => {
            if (attr.id && usedAttributes.has(attr.id)) {
              const values = Array.from(usedAttributes.get(attr.id) || []);
              if (values.length > 0) {
                values.forEach((value) => {
                  narrative.push(`- ${coalesce(attr.name, 'Unknown Attribute')} (attribute_id: ${attr.id}): ${value}`);
                });
              } else {
                narrative.push(`- ${coalesce(attr.name, 'Unknown Attribute')} (attribute_id: ${attr.id})`);
              }
            }
          });
        } else {
          usedAttributes.forEach((values, attrId) => {
            if (values.size > 0) {
              values.forEach((value) => {
                narrative.push(`- Attribute (attribute_id: ${attrId}): ${value}`);
              });
            } else {
              narrative.push(`- Attribute (attribute_id: ${attrId})`);
            }
          });
        }
      }
    }
  }

  // Factors (Categories)
  if (detailedData?.questions) {
    const factors = new Map<number, string>();

    detailedData.questions.forEach((question) => {
      const category = question.question?.category;
      if (category?.id && category.categoryNames?.[0]?.name) {
        factors.set(category.id, category.categoryNames[0].name);
      }
    });

    if (factors.size > 0) {
      narrative.push('');
      narrative.push('FACTORS (CATEGORIES)');
      factors.forEach((name, id) => {
        narrative.push(`- ${name} (factor_id: ${id})`);
      });
    }
  }

  // Questions
  if (detailedData?.questions && detailedData.questions.length > 0) {
    narrative.push('');
    narrative.push('SURVEY QUESTIONS');
    narrative.push(`Total Questions: ${detailedData.questions.length}`);
    narrative.push('');

    const sortedQuestions = [...detailedData.questions].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

    sortedQuestions.forEach((question, index) => {
      const questionText = coalesce(question.question?.messagesPerLanguage?.[0]?.message, 'No question text');
      const questionType = coalesce(question.type?.name, 'Unknown type');
      const categoryName = coalesce(question.question?.category?.categoryNames?.[0]?.name, 'No category');

      // Map question types to user-friendly names
      const questionTypeDisplay =
        {
          SLIDING: 'Scale',
          OPEN: 'Text',
          OPTIONS: 'Multiple Choice',
          MESSAGE: 'Message',
        }[questionType] || questionType;

      narrative.push(`${index + 1}. Question ID: ${question.id}`);
      narrative.push(`   Type: ${questionTypeDisplay}`);
      if (question.question?.category?.id) {
        narrative.push(`   Factor: ${categoryName} (factor_id: ${question.question.category.id})`);
      } else {
        narrative.push(`   Factor: ${categoryName}`);
      }
      narrative.push(`   Text: ${questionText}`);

      // Show scale range for scale questions
      if ((questionType === 'SLIDING' || questionType === 'Scale') && question.question) {
        const lowestRating = question.question.ratingOptionLowest;
        const highestRating = question.question.ratingOptionHighest;
        if (lowestRating !== undefined && highestRating !== undefined) {
          narrative.push(`   Scale Range: ${lowestRating} to ${highestRating}`);
        }
      }

      // Show options for multiple choice questions
      if ((questionType === 'OPTIONS' || questionType === 'Options') && question.question?.choices) {
        narrative.push(`   Options:`);
        question.question.choices.forEach((choice) => {
          const optionText = coalesce(choice.messagesPerLanguage?.[0]?.message, 'No option text');
          narrative.push(`     - ${optionText} (ID: ${choice.id})`);
        });
      }

      // Show attributes used by this question
      if (question.attributes && question.attributes.length > 0) {
        narrative.push(`   Attribute-based: Yes`);
        question.attributes.forEach((attr) => {
          const attrInfo = attributes?.find((a) => a.id === attr.attributeId);
          const attrName = coalesce(attrInfo?.name, 'Unknown attribute');
          if (attr.attributeId) {
            narrative.push(`     - ${attrName} (attribute_id: ${attr.attributeId}): ${attr.attributeValue}`);
          } else {
            narrative.push(`     - ${attrName}: ${attr.attributeValue}`);
          }
        });
      }

      narrative.push('');
    });
  }

  return narrative.join('\n');
}
/* istanbul ignore stop */

function formatDate(dateString: string | number): string {
  try {
    let date: Date;

    // Handle timestamp (number) input
    if (typeof dateString === 'number') {
      date = new Date(dateString);
    } else if (typeof dateString === 'string') {
      // Check if string is a number (timestamp as string)
      const timestamp = parseInt(dateString, 10);
      if (!isNaN(timestamp) && timestamp.toString() === dateString) {
        date = new Date(timestamp);
      } else {
        // Try parsing as date string
        date = new Date(dateString);

        // If invalid, try parsing as ISO string
        if (isNaN(date.getTime())) {
          date = new Date(dateString.replace(' ', 'T') + 'Z');
        }
      }
    } else {
      return String(dateString);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return String(dateString);
    }

    // Format the date
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'UTC',
    });
  } catch {
    return String(dateString);
  }
}

interface SurveyType {
  id: number;
  name?: string;
  threshold?: number;
}

interface SurveyLanguageName {
  id?: number;
  surveyName?: string;
  language?: {
    langISOCode: string;
  };
}

interface Category {
  id?: number;
  categoryNames?: Array<{
    name?: string;
  }>;
}

interface Attribute {
  attributeId?: number;
  attributeValue?: string;
}

interface Question {
  id?: number;
  orderIndex?: number;
  type?: {
    name?: string;
  };
  attributes?: Attribute[];
  question?: {
    id?: number;
    messagesPerLanguage?: Array<{
      message?: string;
      description?: string;
      language?: {
        id: number;
        langISOCode: string;
      };
    }>;
    category?: Category;
    required?: boolean;
    showDescription?: boolean;
    showTextComment?: boolean;
    ratingOptionLowest?: number;
    ratingOptionHighest?: number;
    minSelectedOption?: number;
    maxSelectedOption?: number;
    choices?: Array<{
      id?: number;
      orderIndex?: number;
      messagesPerLanguage?: Array<{
        message?: string;
        language?: {
          id: number;
        };
      }>;
    }>;
  };
}

interface BasicSurveyData {
  id: number;
  name?: string;
  types?: SurveyType[];
  startDate?: string;
  endDate?: string;
  startTimeUTC?: string;
  endTimeUTC?: string;
  anonymousConversation?: boolean;
  isContinuous?: boolean;
  namesPerLanguage?: SurveyLanguageName[];
  recipients?: number;
}

interface DetailedSurveyData {
  id?: number;
  hashCode?: string;
  showResults?: boolean;
  status?: {
    id?: number;
    name?: string;
  };
  anonymousConversation?: boolean;
  namesPerLanguage?: SurveyLanguageName[];
  questions?: Question[];
  startTimeUTC?: string;
  endTimeUTC?: string;
  types?: SurveyType[];
}

interface EmployeeAttribute {
  id?: number;
  name?: string;
  type?: string;
  values?: Array<{
    id?: number;
    value?: string;
  }>;
}
